import { logger } from '@adornis/base/logging.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { getInstitutionByID } from '../api/institution.js';
import { getUserByID } from '../api/users.js';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { versionDiffWorkshopSelSet, getProductByID } from '../api/product.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { Institution } from '../entities/institution.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import { Product } from '../entities/products.js';
import { SettingColorDefintionProp } from '../entities/settings.js';
import type { Workshop } from '../entities/workshop.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { buildProductChangesHtml, buildRegistrationsHTML, type BuildProductChanges } from '../mails/mail-helpers.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { getNotificationType, sendWhatsappWithEmailFallback } from './_helper.js';

defineMailTemplate(MailTemplateType.PRODUCT_CHANGED, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Workshop Änderungen',
    content: `<p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>Es gab Änderungen in folgenden Workshops</p>
<p>{{changed-registrations}}</p>
<p>{{product-changes}}</p>
<p></p>
<p><strong>Sie k&ouml;nnen nicht teilnehmen? Bitte abmelden und dadurch anderen Studierenden die Teilnahme erm&ouml;glichen</strong></p>
<p>Sollten Sie den Termin nicht wahrnehmen k&ouml;nnen, melden Sie sich bitte umgehend in der Anmeldeverwaltung ab. Damit erm&ouml;glichen Sie Studierenden auf der Warteliste die Teilnahme.</p>
<p>Beachten Sie, dass eine Abmeldung bis sp&auml;testens 6 Tage vor Workshop-Beginn erforderlich ist. Danach ist eine Abmeldung nur per E-Mail und mit triftigem Grund m&ouml;glich. Fehlen ohne Absage kann zu einem Ausschluss aus zuk&uuml;nftigen Workshops und zur Berechnung entstandener Kosten f&uuml;hren.</p>
<p>Wir w&uuml;nschen Ihnen viel Freude und Erkenntnisgewinn in den Veranstaltungen. Das ist unser Ansporn und Anspruch! Kontaktieren Sie uns gern <a href="mailto:contact@anmeldung.uni-frankfurt.de">per Mail</a>, wenn Sie ein Anliegen (Fragen, Lob &amp; Kritik) haben.</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>There are changes in following workshops</p>
<p>{{changed-registrations}}</p>
<p>{{product-changes}}</p>
<p></p>
<p>At <strong>"My Profile" </strong>you can unsubscribe from events and also book additional events.</p>
<p>You are not able to attend? Please deregister and thereby allow other students to attend.</p>
<p>If you are unable to attend the scheduled event, please promptly unregister through the enrollment system. This will allow students on the waiting list to participate.</p>
<p>Note that you must unregister no later than 6 days before the workshop starts. After this deadline, unregistration is only possible via email and for valid reasons. Failure to attend without prior notice may result in exclusion from future workshops and the incurrence of associated costs.</p>
<p>We hope you will enjoy the events and gain a lot of knowledge. That is our incentive and claim! Feel free to contact us by e-mail if you have any concerns (questions, praise &amp; criticism).</p>
<p>Best regards</p>`,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.PRODUCT_CHANGED),
  attachments: [],
  displayName: 'Workshop geändert',
  editorHint: () => institutionMailHint(MailTemplateType.PRODUCT_CHANGED),
});

export async function notifyProductChanged(userID: string, productID: string, changes: BuildProductChanges) {
  logger.info({ changes }, 'send change mail');
  // get institution id
  const product = await getProductByID(productID)({
    _id: 1,
    name: 1,
    institutionID: 1,
    topic: 1,
    language: 1,
    slots: { _id: 1, start: 1, end: 1 },
    settings: { courseLocation: 1, inheritedCourseLocation: 1 },
    appointments: { start: 1, name: 1, end: 1, location: 1 },
    ...(versionDiffWorkshopSelSet as any),
  });
  if (!product) throw new Error('product not found');

  const institution = await getInstitutionByID(product.institutionID)({
    ...selectionSet(() => Institution, 1),
    colorDefinitions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  if (!institution) throw new Error('could not find institution');

  // build email
  // set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplate = await getMailTemplateForInstitution(
    product.institutionID,
    MailTemplateType.PRODUCT_CHANGED,
  )(InstitutionMailTemplate.allFields);

  const user = await getUserByID(userID)({
    _id: 1,
    title: 1,
    firstName: 1,
    lastName: 1,
    email: 1,
    whatsappNotification: 1,
    whatsappPhone: 1,
    emailConfirmed: 1,
  });
  if (!user) throw new Error('user not found ' + userID);

  const participation = await loadMongoData(Participation, { productID, userID }).then(ps => ps.at(0));
  if (!participation) throw new Error('could not find participation, user:' + userID + ' product: ' + productID);
  participation.product = product;

  const token = await generateLoginToken({
    userID: user._id,
    userClass: 'User',
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const sendEmail = async () => {
    const mailVars = {
      [MailTemplateVars.TITLE]: user.title ?? '',
      [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
      [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
      [MailTemplateVars.PRODUCT_NAME]: product.name,
      [MailTemplateVars.CHANGED_REGISTRATIONS]: buildRegistrationsHTML([
        {
          name: product.name,
          state: participation.state,
          topic: product.topic,
          language: product.language,
          waitingListPlace: null,
          appointments: (product as Workshop).appointments || [],
          englishVersion: false,
        },
      ]),
      [MailTemplateVars.CHANGED_REGISTRATIONS_EN]: buildRegistrationsHTML([
        {
          name: product.name,
          state: participation.state,
          topic: product.topic,
          language: product.language,
          waitingListPlace: null,
          appointments: (product as Workshop).appointments || [],
          englishVersion: true,
        },
      ]),
      [MailTemplateVars.PRODUCT_CHANGES]: buildProductChangesHtml(product as Product, changes, false),
      [MailTemplateVars.PRODUCT_CHANGES_EN]: buildProductChangesHtml(product as Product, changes, true),
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
    };

    const { subject, content } = (await assembleInstitutionMail({
      template: mailTemplate,
      infos: mailVars as any,
    })) as { subject: string; content: any };

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    await sendInstitutionEmail({
      email: user.email,
      subject,
      content,
      institutionID: product.institutionID,
    });
  };

  const notificationType = await getNotificationType(user, mailTemplate, product.institutionID);
  if (notificationType === 'whatsapp') {
    await sendWhatsappWithEmailFallback({
      institutionID: product.institutionID,
      user: { _id: user._id, whatsappPhone: user.whatsappPhone!, email: user.email },
      mailTemplate,
      mailTemplateType: MailTemplateType.PRODUCT_CHANGED,
      parameters: [
        InstitutionMailTemplate.mapFirstNameVar(user) || '-',
        InstitutionMailTemplate.mapLastNameVar(user) || '-',
        product.name || '-',
      ],
      sendEmail,
    });
  } else if (notificationType === 'email') {
    await sendEmail();
  }
}
